import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { webPortalTarget } from '../platforms/web/desktop/webPortalTarget';
import { ChevronDown, Clock3, History, X } from 'lucide-react';
import { healthModulesApi } from '../api';
import './HealthRecordDialog.css';

export type HealthRecordKind = 'bp' | 'weight' | 'sugar' | 'hr';
export type VitalRecordKind = Extract<HealthRecordKind, 'bp' | 'hr'>;

interface Props {
  kind: HealthRecordKind;
  variant?: 'quick' | 'full';
  onClose: () => void;
  onSaved: (summary: string) => void | Promise<void>;
  onOpenHistory?: (kind: VitalRecordKind) => void;
}

const TITLES: Record<HealthRecordKind, string> = {
  bp: '血压',
  weight: '体重与体成分',
  sugar: '血糖',
  hr: '心率',
};

function toLocalDatetimeValue(date = new Date()) {
  const pad = (value: number) => String(value).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

function inferPeriod(value: string) {
  const hour = new Date(value).getHours();
  if (!Number.isFinite(hour)) return 'morning';
  if (hour >= 5 && hour < 12) return 'morning';
  if (hour >= 12 && hour < 18) return 'afternoon';
  if (hour >= 18 && hour < 23) return 'evening';
  return 'before_sleep';
}

function initialForm() {
  const measuredAt = toLocalDatetimeValue();
  return {
    systolic: '', diastolic: '', heart_rate: '',
    weight: '', height: '', body_fat_pct: '', muscle_mass: '',
    value: '', measurement_type: 'fasting', state: 'resting',
    period: inferPeriod(measuredAt), measured_at: measuredAt, note: '',
  };
}

export default function HealthRecordDialog({ kind, variant = 'full', onClose, onSaved, onOpenHistory }: Props) {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [moreOpen, setMoreOpen] = useState(variant === 'full');
  const [form, setForm] = useState<Record<string, string>>(initialForm);
  const dialogRef = useRef<HTMLElement>(null);
  const quick = variant === 'quick' && (kind === 'bp' || kind === 'hr');
  const title = TITLES[kind];

  useEffect(() => {
    const previousFocus = document.activeElement as HTMLElement | null;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const timer = window.setTimeout(() => dialogRef.current?.querySelector<HTMLElement>('input, button, select')?.focus(), 0);
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
      if (event.key !== 'Tab' || !dialogRef.current) return;
      const focusable = Array.from(dialogRef.current.querySelectorAll<HTMLElement>('button:not(:disabled), input:not(:disabled), select:not(:disabled), textarea:not(:disabled), [tabindex]:not([tabindex="-1"])'));
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      window.clearTimeout(timer);
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = previousOverflow;
      previousFocus?.focus?.();
    };
  }, [onClose]);

  const setField = (name: string, value: string) => {
    setError('');
    setForm((current) => ({
      ...current,
      [name]: value,
      ...(name === 'measured_at' && current.period !== 'unknown' ? { period: inferPeriod(value) } : {}),
    }));
  };

  const submitLabel = useMemo(() => saving ? '保存中…' : `保存${title}`, [saving, title]);

  const validate = () => {
    if (kind === 'bp') {
      const systolic = Number(form.systolic);
      const diastolic = Number(form.diastolic);
      const pulse = form.heart_rate ? Number(form.heart_rate) : null;
      if (!Number.isFinite(systolic) || systolic < 60 || systolic > 260) return '收缩压请输入 60–260 之间的数值';
      if (!Number.isFinite(diastolic) || diastolic < 30 || diastolic > 160) return '舒张压请输入 30–160 之间的数值';
      if (pulse !== null && (!Number.isFinite(pulse) || pulse < 30 || pulse > 220)) return '测量时心率请输入 30–220 之间的数值';
    }
    if (kind === 'hr') {
      const heartRate = Number(form.heart_rate);
      if (!Number.isFinite(heartRate) || heartRate < 30 || heartRate > 220) return '心率请输入 30–220 之间的数值';
    }
    if (kind === 'weight') {
      const weight = Number(form.weight);
      if (!form.weight || !Number.isFinite(weight) || weight < 20 || weight > 300) return '体重请输入 20–300 kg 之间的数值';
      if (form.height && (Number(form.height) < 80 || Number(form.height) > 250)) return '身高请输入 80–250 cm 之间的数值';
      if (form.body_fat_pct && (Number(form.body_fat_pct) < 1 || Number(form.body_fat_pct) > 70)) return '体脂率请输入 1–70% 之间的数值';
      if (form.muscle_mass && (Number(form.muscle_mass) < 1 || Number(form.muscle_mass) > 150)) return '肌肉量请输入 1–150 kg 之间的数值';
    }
    if (kind === 'sugar') {
      const value = Number(form.value);
      if (!form.value || !Number.isFinite(value) || value < 1 || value > 35) return '血糖请输入 1–35 mmol/L 之间的数值';
    }
    if (form.measured_at && Number.isNaN(new Date(form.measured_at).getTime())) return '请选择有效的测量时间';
    return '';
  };

  const submit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const validationError = validate();
    if (validationError) { setError(validationError); return; }
    setSaving(true);
    setError('');
    try {
      const measured_at = form.measured_at ? new Date(form.measured_at).toISOString() : undefined;
      let summary = `${title}记录已保存`;
      if (kind === 'bp') {
        await healthModulesApi.addBpRecord({
          systolic: Number(form.systolic), diastolic: Number(form.diastolic),
          heart_rate: form.heart_rate ? Number(form.heart_rate) : undefined,
          period: form.period, measured_at, note: form.note,
        });
        summary = `已记录 ${form.systolic}/${form.diastolic} mmHg`;
      } else if (kind === 'weight') {
        await healthModulesApi.addWeightRecord({
          weight: Number(form.weight), height: form.height ? Number(form.height) : undefined,
          body_fat_pct: form.body_fat_pct ? Number(form.body_fat_pct) : undefined,
          muscle_mass: form.muscle_mass ? Number(form.muscle_mass) : undefined,
          measured_at, note: form.note,
        });
      } else if (kind === 'sugar') {
        await healthModulesApi.addSugarRecord({ value: Number(form.value), measurement_type: form.measurement_type, measured_at, note: form.note });
      } else {
        await healthModulesApi.addHrRecord({ heart_rate: Number(form.heart_rate), state: form.state, measured_at, note: form.note });
        const stateLabel = form.state === 'active' ? '活动中' : form.state === 'post_exercise' ? '运动后' : form.state === 'resting' ? '静息' : '未注明';
        summary = `已记录 ${form.heart_rate} 次/分 · ${stateLabel}`;
      }
      await onSaved(summary);
      onClose();
    } catch (requestError: any) {
      setError(requestError?.response?.data?.detail || '保存失败，请检查填写内容');
    } finally {
      setSaving(false);
    }
  };

  return createPortal((
    <div className="hrd-backdrop" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
      <section ref={dialogRef} className={`hrd-dialog ${quick ? 'quick' : ''}`} role="dialog" aria-modal="true" aria-labelledby="hrd-title">
        <header className="hrd-header">
          <div><span>{quick ? '快速记录' : '手动记录'}</span><h2 id="hrd-title">{title}</h2></div>
          <div className="hrd-header-actions">
            {(kind === 'bp' || kind === 'hr') && onOpenHistory && (
              <button type="button" className="hrd-history-link" onClick={() => onOpenHistory(kind)}><History size={16} />历史趋势</button>
            )}
            <button type="button" className="hrd-icon-button" onClick={onClose} aria-label="关闭"><X size={19} /></button>
          </div>
        </header>

        <form onSubmit={submit} noValidate>
          {kind === 'bp' && (
            <div className={quick ? 'hrd-vitals-grid' : 'hrd-form-grid'}>
              <label><span>收缩压</span><div className="hrd-input-wrap"><input autoFocus required inputMode="numeric" type="number" min="60" max="260" value={form.systolic} onChange={(event) => setField('systolic', event.target.value)} aria-describedby="hrd-bp-help" /><small>SYS</small></div><em>mmHg</em></label>
              <label><span>舒张压</span><div className="hrd-input-wrap"><input required inputMode="numeric" type="number" min="30" max="160" value={form.diastolic} onChange={(event) => setField('diastolic', event.target.value)} aria-describedby="hrd-bp-help" /><small>DIA</small></div><em>mmHg</em></label>
              <label><span>测量时心率</span><div className="hrd-input-wrap"><input inputMode="numeric" type="number" min="30" max="220" value={form.heart_rate} onChange={(event) => setField('heart_rate', event.target.value)} aria-describedby="hrd-pulse-help" /><small>PULSE</small></div><em>可选 · 次/分</em></label>
            </div>
          )}

          {kind === 'hr' && (
            <div className={quick ? 'hrd-heart-rate-block' : 'hrd-form-grid'}>
              <label className="hrd-heart-rate-input"><span>心率</span><div className="hrd-input-wrap"><input autoFocus required inputMode="numeric" type="number" min="30" max="220" value={form.heart_rate} onChange={(event) => setField('heart_rate', event.target.value)} /><small>BPM</small></div></label>
              <fieldset className="hrd-state-fieldset"><legend>测量状态</legend><div className="hrd-segments">
                {[['resting', '静息'], ['active', '活动中'], ['post_exercise', '运动后'], ['unknown', '未注明']].map(([value, label]) => (
                  <button key={value} type="button" className={form.state === value ? 'active' : ''} onClick={() => setField('state', value)} aria-pressed={form.state === value}>{label}</button>
                ))}
              </div></fieldset>
            </div>
          )}

          {kind === 'weight' && <div className="hrd-form-grid">
            <label><span>体重</span><div className="hrd-input-wrap"><input required type="number" min="20" max="300" step="0.1" value={form.weight} onChange={(event) => setField('weight', event.target.value)} /><small>kg</small></div></label>
            <label><span>身高</span><div className="hrd-input-wrap"><input type="number" min="80" max="250" step="0.1" value={form.height} onChange={(event) => setField('height', event.target.value)} /><small>cm</small></div></label>
            <label><span>体脂率</span><div className="hrd-input-wrap"><input type="number" min="1" max="70" step="0.1" value={form.body_fat_pct} onChange={(event) => setField('body_fat_pct', event.target.value)} /><small>%</small></div></label>
            <label><span>肌肉量</span><div className="hrd-input-wrap"><input type="number" min="1" max="150" step="0.1" value={form.muscle_mass} onChange={(event) => setField('muscle_mass', event.target.value)} /><small>kg</small></div></label>
          </div>}

          {kind === 'sugar' && <div className="hrd-form-grid">
            <label><span>血糖</span><div className="hrd-input-wrap"><input required type="number" min="1" max="35" step="0.1" value={form.value} onChange={(event) => setField('value', event.target.value)} /><small>mmol/L</small></div></label>
            <label><span>测量场景</span><select value={form.measurement_type} onChange={(event) => setField('measurement_type', event.target.value)}><option value="fasting">空腹</option><option value="before_meal">餐前</option><option value="after_meal_1h">餐后1小时</option><option value="after_meal_2h">餐后2小时</option><option value="random">随机</option><option value="bedtime">睡前</option><option value="unknown">未注明</option></select></label>
          </div>}

          {quick && <button type="button" className="hrd-more-toggle" onClick={() => setMoreOpen((current) => !current)} aria-expanded={moreOpen}><Clock3 size={15} />{moreOpen ? '收起更多选项' : '时间与备注'}<ChevronDown className={moreOpen ? 'rotated' : ''} size={16} /></button>}

          {moreOpen && <div className="hrd-more-fields">
            {kind === 'bp' && <label><span>测量时段</span><select value={form.period} onChange={(event) => setField('period', event.target.value)}><option value="morning">晨起</option><option value="afternoon">午后</option><option value="evening">晚间</option><option value="before_sleep">睡前</option><option value="unknown">未注明</option></select></label>}
            <label><span>测量时间</span><input type="datetime-local" value={form.measured_at} onChange={(event) => setField('measured_at', event.target.value)} /></label>
            <label className="wide"><span>备注</span><textarea rows={2} value={form.note} onChange={(event) => setField('note', event.target.value)} placeholder="可选：测量场景、设备或身体感受" /></label>
          </div>}

          <p id="hrd-bp-help" className="hrd-help">{kind === 'bp' && '按血压计显示依次录入；测量时心率仅随本次血压保存。'}</p>
          {kind === 'bp' && <span id="hrd-pulse-help" className="hrd-sr-only">血压测量时心率可选，范围 30 到 220 次每分钟</span>}
          {error && <p className="hrd-error" role="alert">{error}</p>}
          <footer className="hrd-footer"><button type="button" className="secondary" onClick={onClose}>取消</button><button type="submit" className="primary" disabled={saving}>{submitLabel}</button></footer>
        </form>
      </section>
    </div>
  ), webPortalTarget());
}
